<template>
  <AppCard class="help-card">
    <div
      class="help-card-header t-flex t-flex-align-items-center t-flex-justify-content-space-between"
    >
      <h4 class="is-heading-4">{{ t("gettingStarted.title") }}</h4>
      <scale-button @click="updateIsShowWizardGettingStartedPopup(true)">
        {{ t("gettingStarted.openWizard") }}
      </scale-button>
    </div>
    <div class="help-card-content">
      <ul class="t-flex-inline t-flex-wrap t-flex-justify-content-center help-card-items">
        <li
          v-for="item in GETTING_STARTED_ITEMS"
          :key="item.id"
          class="help-card-item t-flex t-flex-align-items-center"
          :class="{ 'is-disabled': item.disabled }"
        >
          <span class="t-flexShrink0">
            <component
              :is="item.icon"
              :name="item.icon"
              :accessibility-title="item.accessibilityTitle"
            />
          </span>
          <span class="title">{{ item.title }}</span>
          <scale-loading-spinner v-if="isLoading" />
          <span class="t-flexShrink0 count" v-else>{{ item.value }}</span>
        </li>
      </ul>
    </div>
  </AppCard>
</template>

<script setup lang="ts">
import { isCxApp } from "@/common/util/env"
import AppCard from "@/components/global/TCard/TCard.vue"
import { usePolicies } from "@/modules/govern/non-cx/policies/store/usePolicies"
import { useSubscribedAgreements } from "@/modules/govern/non-cx/subscribedAgreements/store/useSubscribedAgreements"
import { useUploadHistoryStore } from "@/modules/upload-history/store/useUploadHistory"
import { useProviderStore } from "@/store/modules/provider/providerStore"
import { storeToRefs } from "pinia"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import useHomeStore from "../non-cx/store/useHomeStore"
const { t } = useI18n()

const { fetchAspectsCount, updateIsShowWizardGettingStartedPopup, fetchConectorsCount } =
  useProviderStore()
const { aspectsCountData, connectorCountData } = storeToRefs(useProviderStore())
const isLoading = ref<boolean>(false)

const GETTING_STARTED_ITEMS = computed(() => [
  {
    id: 1,
    icon: "scale-icon-device-server",
    accessibilityTitle: "server",
    title: t("gettingStarted.yourAssets"),
    value: isCxApp() ? aspectsCountData?.value : historyData.value.total,
    disabled: false
  },
  {
    id: 2,
    icon: "scale-icon-alert-compliance",
    accessibilityTitle: "compliance",
    title: t("gettingStarted.yourPolicies"),
    value: isCxApp() ? 0 : policyList?.value?.total,
    disabled: isCxApp() ? true : false
  },
  {
    id: 3,
    icon: "scale-icon-communication-message-outgoing",
    accessibilityTitle: "message-outgoing",
    title: t("gettingStarted.outgoingAgreements"),
    value: isCxApp() ? 0 : providerContracts?.value?.length,
    disabled: isCxApp() ? true : false
  },
  {
    id: 4,
    icon: "scale-icon-communication-message-incoming",
    accessibilityTitle: "message-incoming",
    title: t("gettingStarted.incomingAgreements"),
    value: 0,
    disabled: true
  },
  {
    id: 5,
    icon: "scale-icon-device-device-ethernet",
    accessibilityTitle: "external-link",
    title: t("gettingStarted.connectors"),
    value: isCxApp() ? connectorCountData?.value : connectorData?.value?.length,
    disabled: false
  }
])
const { connectorData } = storeToRefs(useSubscribedAgreements())
const { fetchConnectors } = useSubscribedAgreements()
const { fetchPolicies } = usePolicies()
const { policyList } = storeToRefs(usePolicies())
const { fetchUploadHistory } = useUploadHistoryStore()
const { historyData } = storeToRefs(useUploadHistoryStore())
const { providerContracts } = storeToRefs(useHomeStore())
const { fetchProviderContracts } = useHomeStore()

function fetchData() {
  isLoading.value = true
  if (isCxApp()) {
    Promise.all([fetchAspectsCount(), fetchConectorsCount()]).finally(() => {
      isLoading.value = false
    })
  } else {
    Promise.all([
      fetchConnectors(),
      fetchPolicies(),
      fetchUploadHistory(),
      fetchProviderContracts()
    ]).finally(() => {
      isLoading.value = false
    })
  }
}
fetchData()
</script>

<style scoped lang="scss">
.help-card {
  &-content {
    padding: calc(var(--scl-spacing-24) * 3) 0;
  }
}
.help-card-items {
  gap: var(--scl-spacing-16);
}
.help-card-item {
  border: 1px solid var(--telekom-color-ui-border-standard);
  border-radius: var(--scl-radius-8);
  padding: var(--scl-spacing-24) var(--scl-spacing-16);
  gap: var(--scl-spacing-12);

  &.is-disabled {
    color: var(--telekom-color-text-and-icon-disabled);
    .count {
      background: var(--telekom-color-text-and-icon-disabled);
    }
  }

  .title {
    font: var(--telekom-text-style-body-bold);
    font-weight: var(--telekom-typography-font-weight-extra-bold);
  }

  .count {
    width: var(--scl-spacing-24);
    height: var(--scl-spacing-24);
    display: inline-flex;
    justify-content: center;
    align-items: center;
    background: var(--telekom-color-text-and-icon-primary-standard);
    font: var(--telekom-text-style-body-bold);
    color: var(--telekom-color-ui-base);
    border-radius: var(--telekom-radius-circle);
    overflow: hidden;
  }
}
</style>
